import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { List } from 'immutable';

import { AutoSuggestion } from '../helpers/AutoSuggestion';
import config from '../../config';

/**
 * Autocomplete filter of the user list. The filter is applied when a suggestion is selected or the text is cleared.
 *
 * @param {object} props Props.
 * @param {string} props.name Filter name of the selected user name.
 * @param {string} props.idName Filter name of the selected user id.
 * @param {string} props.placeholder Placeholder.
 * @param {string} props.value Applied filter value.
 * @param {Immutable.List} props.users Users to build suggestions from.
 * @param {Function} props.getSuggestions Function to get suggestions by users and entered text.
 * @param {Function} props.onChange Handle filter change, called with `{ [name]: userName, [idName]: userId }`.
 */
const UserFilterAutocompleteComponent = ({ name, idName, placeholder, value, users, getSuggestions, onChange }) => {
  const [text, setText] = useState(value);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);

  useEffect(() => {
    setText(value);
  }, [value]);

  const suggestions = useMemo(() => (isSuggestionOpen ? getSuggestions(users, text) : List()), [
    isSuggestionOpen,
    getSuggestions,
    users,
    text,
  ]);

  const handleTextChange = useCallback(
    ({ event }) => {
      const nextText = event.target.value;

      setText(nextText);
      setIsSuggestionOpen(nextText.length > 0);

      if (!nextText && value) {
        onChange({ [name]: '', [idName]: '' });
      }
    },
    [name, idName, value, onChange],
  );

  const handleSuggestionSelect = useCallback(
    ({ suggestion }) => {
      const suggestionName = suggestion.get('name');

      setText(suggestionName);
      setIsSuggestionOpen(false);
      onChange({ [name]: suggestionName, [idName]: suggestion.get('id') });
    },
    [name, idName, onChange],
  );

  const handleSuggestionClose = useCallback(() => {
    setText(value);
    setIsSuggestionOpen(false);
  }, [value]);

  return (
    <AutoSuggestion
      className="form-control input-sm"
      mode={isSuggestionOpen ? config.SUGGEST_MODE : config.VIEW_MODE}
      name={name}
      onBlur={handleSuggestionClose}
      onSuggestionClose={handleSuggestionClose}
      onSuggestionSelect={handleSuggestionSelect}
      onTextChange={handleTextChange}
      placeholder={placeholder}
      suggestions={suggestions}
      value={text}
    />
  );
};

UserFilterAutocompleteComponent.propTypes = {
  getSuggestions: PropTypes.func.isRequired,
  idName: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  users: PropTypes.instanceOf(List).isRequired,
  value: PropTypes.string.isRequired,
};

export const UserFilterAutocomplete = memo(UserFilterAutocompleteComponent);
